import React, { useState, useMemo, useEffect } from 'react';
import { ATTENDANCE_TYPES } from '../mock';
import { apiListAttendance } from '../api';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Search, MapPin, Clock, User, CalendarDays, CheckCircle2, Coffee, LogIn, LogOut, Download } from 'lucide-react';

const ICONS = { LogIn, LogOut, Coffee };

const fmtTime = (iso) => new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
const fmtDate = (iso) => new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });

export const AttendanceDailyView = () => {
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => { apiListAttendance().then(setEvents).catch(() => setEvents([])); }, []);

  // Group by employee for selected date
  const byEmployee = useMemo(() => {
    const dayEvents = events.filter(e => e.timestamp.startsWith(date));
    const map = {};
    for (const e of dayEvents) {
      if (!map[e.employeeEmail]) map[e.employeeEmail] = { name: e.employeeName, email: e.employeeEmail, workplace: e.workplaceName, events: [] };
      map[e.employeeEmail].events.push(e);
    }
    const list = Object.values(map);
    list.forEach(emp => emp.events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
    return list.filter(emp => {
      if (!search) return true;
      const q = search.toLowerCase();
      return emp.name.toLowerCase().includes(q) || emp.email.toLowerCase().includes(q);
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [events, date, search]);

  const computeStatus = (empEvents) => {
    if (!empEvents.length) return { label: 'Assente', color: 'slate' };
    const last = empEvents[empEvents.length - 1];
    if (last.type === 'entrata') return { label: 'Presente', color: 'emerald' };
    if (last.type === 'pausa_inizio') return { label: 'In pausa', color: 'amber' };
    if (last.type === 'pausa_fine') return { label: 'Presente', color: 'emerald' };
    if (last.type === 'uscita') return { label: 'Uscito', color: 'blue' };
    return { label: '-', color: 'slate' };
  };

  const computeHours = (empEvents) => {
    const entrata = empEvents.find(e => e.type === 'entrata');
    const uscita = [...empEvents].reverse().find(e => e.type === 'uscita');
    if (!entrata) return 0;
    const end = uscita ? new Date(uscita.timestamp) : new Date();
    const totalHours = (end - new Date(entrata.timestamp)) / 3600000;
    // Subtract 1 hour default lunch break if worked more than 6 hours
    return Math.max(0, totalHours >= 6 ? totalHours - 1 : totalHours);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-[170px] text-sm" />
          <div className="text-xs text-slate-500">
            {byEmployee.length} {byEmployee.length === 1 ? 'dipendente' : 'dipendenti'} con timbrature
          </div>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca dipendente..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-8 w-[220px] text-sm" />
        </div>
      </div>

      {byEmployee.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
          <CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-700">Nessuna timbratura in questa data</div>
        </div>
      ) : (
        <div className="space-y-3">
          {byEmployee.map(emp => {
            const st = computeStatus(emp.events);
            const hours = computeHours(emp.events);
            return (
              <div key={emp.email} className="p-4 rounded-xl border border-slate-200 bg-white">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center text-xs font-semibold">
                      {emp.name.split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-slate-900">{emp.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{emp.workplace}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-${st.color}-50 text-${st.color}-800 border border-${st.color}-200`}>
                      <div className={`w-1.5 h-1.5 rounded-full bg-${st.color}-500`} />
                      {st.label}
                    </div>
                    {hours > 0 && (
                      <div className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-full font-medium tabular-nums">
                        <Clock className="w-3 h-3 inline mr-1" />{hours.toFixed(1)}h
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {emp.events.map(e => {
                    const meta = ATTENDANCE_TYPES[e.type];
                    const Icon = ICONS[meta.icon] || Clock;
                    return (
                      <div key={e.id} className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-${meta.color}-50 text-${meta.color}-800 border border-${meta.color}-200`}>
                        <Icon className="w-3 h-3" />
                        <span className="font-medium">{meta.label}</span>
                        <span className="tabular-nums">{fmtTime(e.timestamp)}</span>
                        <span className="text-slate-500 text-[10px]">{e.distance}m</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const AttendanceMonthlyReport = () => {
  const [events, setEvents] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [employee, setEmployee] = useState('all');

  useEffect(() => { apiListAttendance().then(setEvents).catch(() => setEvents([])); }, []);

  const employees = useMemo(() => {
    const set = new Map();
    events.forEach(e => set.set(e.employeeEmail, e.employeeName));
    return Array.from(set.entries()).map(([email, name]) => ({ email, name }));
  }, [events]);

  const report = useMemo(() => {
    const monthEvents = events.filter(e => e.timestamp.startsWith(month) && (employee === 'all' || e.employeeEmail === employee));
    const byEmp = {};
    for (const e of monthEvents) {
      if (!byEmp[e.employeeEmail]) byEmp[e.employeeEmail] = { name: e.employeeName, email: e.employeeEmail, days: {} };
      const day = e.timestamp.slice(0, 10);
      if (!byEmp[e.employeeEmail].days[day]) byEmp[e.employeeEmail].days[day] = [];
      byEmp[e.employeeEmail].days[day].push(e);
    }
    return Object.values(byEmp).map(emp => {
      let totalHours = 0;
      let workedDays = 0;
      Object.values(emp.days).forEach(dayEvents => {
        dayEvents.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        const entrata = dayEvents.find(e => e.type === 'entrata');
        const uscita = [...dayEvents].reverse().find(e => e.type === 'uscita');
        if (entrata && uscita) {
          const hours = (new Date(uscita.timestamp) - new Date(entrata.timestamp)) / 3600000;
          // Subtract 1 hour default lunch break if worked more than 6 hours
          totalHours += Math.max(0, hours >= 6 ? hours - 1 : hours);
          workedDays++;
        }
      });
      return { ...emp, totalHours, workedDays, daysCount: Object.keys(emp.days).length };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [events, month, employee]);

  const exportCSV = () => {
    const header = 'Dipendente,Email,Giorni lavorati,Giorni timbrati,Ore totali\n';
    const rows = report.map(r => `${r.name},${r.email},${r.workedDays},${r.daysCount},${r.totalHours.toFixed(2)}`).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `presenze_${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-[170px] text-sm" />
          <Select value={employee} onValueChange={setEmployee}>
            <SelectTrigger className="h-9 w-[220px] text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i dipendenti</SelectItem>
              {employees.map(e => <SelectItem key={e.email} value={e.email}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={exportCSV} className="gap-2 h-9" disabled={report.length === 0}>
          <Download className="w-4 h-4" /> Esporta CSV
        </Button>
      </div>

      {report.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
          <CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-700">Nessun dato per il periodo selezionato</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Dipendente</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Giorni timbrati</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Giorni completi</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Ore totali</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {report.map(r => (
                <tr key={r.email} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.email}</div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900">{r.daysCount}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900">{r.workedDays}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-900 font-semibold tabular-nums">
                      {r.totalHours.toFixed(1)}h
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
